import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { cartao, cor, texto } from '#components/finapp/estilo';

type Props = {
  /** Itens na fila inteira (precisa agora + sem categoria). */
  total: number;
  /** Soma ABSOLUTA, em centavos, do que está sem categoria. */
  totalSemCategoria: number;
};

/**
 * O topo da tela **Pendências** — e o PROTAGONISTA dela.
 *
 * ⭐ Regra nº 5 da direção "Extrato": um número manda na tela. Aqui é
 * *quantos itens a fila tem* em `texto.valorGrande` (40px, a serifa), com o
 * total em R$ sem categoria como legenda — nunca dois números do mesmo
 * tamanho disputando o olho.
 *
 * ⚠️ Fila vazia é o estado SAUDÁVEL (spec §5.4), não um erro: nada de
 * ilustração de alerta, nada de call-to-action inventada ("conecte um
 * banco!"). O texto só diz que não há nada esperando — que é a verdade, e é
 * o que o dono precisa saber.
 *
 * ⚠️ E esse é o estado do dia 1: enquanto nenhum banco estiver conectado, a
 * fila nasce vazia. Por isso o texto de vazio não pode soar como defeito.
 */
export function ResumoDaFila({ total, totalSemCategoria }: Props) {
  const { t } = useTranslation();

  if (total === 0) {
    return (
      <View style={{ ...cartao, gap: 6 }}>
        <Text style={texto.rotuloCartao}>
          <Trans>Na fila</Trans>
        </Text>
        <Text data-testid="pendencias-vazio" style={texto.titulo}>
          <Trans>Nada esperando por você.</Trans>
        </Text>
        <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
          <Trans>
            Quando chegar lançamento sem categoria, conta fixa vencida ou banco
            desconectado, aparece aqui.
          </Trans>
        </Text>
      </View>
    );
  }

  return (
    <View style={{ ...cartao, gap: 6 }}>
      {/*
       * ⚠️ "Na fila", não "Pendências": o cabeçalho da página já diz
       * Pendências, e repetir a mesma palavra dois centímetros abaixo é
       * ruído — além de deixar a tela ambígua pra quem procura um texto
       * (dois "Pendências" na mesma tela).
       */}
      <Text style={texto.rotuloCartao}>
        <Trans>Na fila</Trans>
      </Text>
      <Text data-testid="pendencias-total" style={texto.valorGrande}>
        {total}
      </Text>
      <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
        {/*
         * ⚠️ Sem plural i18n (`{{count}}`) de propósito: o número já está
         * dito, enorme, na linha de cima — repetir "9 itens" aqui só
         * disputaria com o protagonista, e ainda amarraria a tela a chaves
         * de pluralização que não existem no nosso `pt-BR`.
         */}
        {totalSemCategoria > 0
          ? t('esperando por você · {{valor}} sem categoria', {
              valor: `R$ ${integerToCurrency(totalSemCategoria)}`,
            })
          : t('esperando por você')}
      </Text>
    </View>
  );
}
